import { useState } from "react";
import { toast } from "sonner";
import { Search, Upload, RotateCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

/**
 * GotSport public-schedule import (iter32e — Custom tournaments only).
 *
 * Flow:
 *   1. Admin enters Event ID + clicks "Find divisions"
 *   2. Backend fetches all divisions; we render a dropdown
 *   3. Admin picks division + clicks "Preview"
 *   4. Backend returns a summary (team count, match count, sample teams)
 *   5. Admin confirms "Import + wipe existing data" → backend wipes and replaces
 *
 * Surface (small intentionally): only one of {Empty, Picked, Preview, Imported}
 * is shown at a time so the admin doesn't lose context.
 */
export default function GotSportImportCard({ leagueId, tournament, onImported }) {
    const [eventId, setEventId] = useState(tournament?.gotsport_event_id || "");
    const [event, setEvent] = useState(null);
    const [selectedGroupId, setSelectedGroupId] = useState(tournament?.gotsport_group_id || "");
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(null);
    // iter87.172 — In-app confirm modal replaces native window.confirm()
    // which mobile Safari + Chrome silently suppressed on this surface
    // (empty-action click, no dialog). State-based modal always renders.
    const [confirmOpen, setConfirmOpen] = useState(false);

    const handleFindDivisions = async () => {
        if (!eventId) { toast.error("Enter a GotSport Event ID first."); return; }
        setBusy("find");
        try {
            const r = await api.gotsportListDivisions(eventId);
            setEvent(r);
            setSelectedGroupId(r.divisions[0]?.group_id || "");
            setPreview(null);
            toast.success(`Found ${r.divisions.length} division(s) in "${r.event_name}".`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not fetch event.");
        } finally { setBusy(null); }
    };

    const handlePreview = async () => {
        if (!selectedGroupId) { toast.error("Pick a division first."); return; }
        setBusy("preview");
        try {
            const r = await api.gotsportPreview(leagueId, Number(eventId), Number(selectedGroupId), tournament?.id);
            setPreview(r);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Preview failed.");
        } finally { setBusy(null); }
    };

    const handleImport = async () => {
        if (!preview) return;
        setConfirmOpen(false);
        setBusy("import");
        try {
            const r = await api.gotsportImport(
                leagueId, Number(eventId), Number(selectedGroupId),
                preview.division_name, tournament?.id,
            );
            toast.success(`Imported ${r.team_count} teams + ${r.match_count} matches.`);
            setPreview(null);
            onImported?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Import failed.");
        } finally { setBusy(null); }
    };

    const [debug, setDebug] = useState(null);
    const handleRefresh = async () => {
        setBusy("refresh");
        try {
            const r = await api.gotsportRefresh(leagueId, tournament?.id);
            const msg = r.updated_scores > 0
                ? `Updated ${r.updated_scores} score(s) from GotSport.`
                : "Already up to date.";
            toast.success(msg);
            // iter87.180.7 — Capture the diagnostic payload so admins on
            // mobile (no dev tools) can verify the scraper is reading
            // the correct GotSport columns for their tournament.
            setDebug({
                synced_at: r.synced_at,
                standings_row_count: r.standings_row_count,
                standings: r.standings_debug || [],
            });
            onImported?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Refresh failed.");
        } finally { setBusy(null); }
    };

    const alreadyImported = tournament?.gotsport_event_id;

    return (
        <div className="brutal-card p-4 space-y-3" data-testid="gotsport-import-card">
            <div className="flex items-center gap-2">
                <Upload className="w-4 h-4 text-neon" />
                <h3 className="font-display font-extrabold uppercase text-sm">GotSport Import</h3>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-auto">Custom only</span>
            </div>
            <p className="text-[11px] text-zinc-500">
                Import a GotSport tournament division. Enter the event ID, pick a division,
                preview, and confirm. Importing <span className="text-volt font-bold">wipes</span>
                {" "}any existing teams + matches in this tournament.
            </p>

            {alreadyImported && (
                <div className="brutal-card-light p-2 text-xs space-y-1" data-testid="gotsport-current">
                    <div className="text-[10px] uppercase tracking-widest text-neon font-bold">Currently linked</div>
                    <div>Event <span className="font-mono">{tournament.gotsport_event_id}</span> · <span className="font-bold">{tournament.gotsport_division_name}</span></div>
                    <div className="text-[10px] text-zinc-500">Last synced: {tournament.gotsport_last_synced_at ? new Date(tournament.gotsport_last_synced_at).toLocaleString() : "never"}</div>
                    {/* iter87.180.8 — Show the last poll skip reason
                        directly on the panel so admins understand why
                        auto-sync isn't firing. Populated by the poller
                        every 60s. Common reasons: `outside_dates`
                        (start/end don't include today), `too_soon`
                        (interval hasn't elapsed yet), `league_disabled`
                        (site-admin toggle off), `tournament_finished`
                        (every match played), `tenant_capped`. */}
                    {tournament.gotsport_last_poll_reason && (() => {
                        // iter87.180.13 — `too_soon` is a NORMAL wait state,
                        // not an error. Show it in neutral copy with a
                        // helpful "Next auto-sync at ~X:XX" hint. Real
                        // blockers (outside_dates, league_disabled,
                        // tenant_capped, tournament_finished) stay red
                        // so the admin actually notices them.
                        const reason = tournament.gotsport_last_poll_reason;
                        const isBenign = reason === "too_soon";
                        const intervalMin = tournament.gotsport_auto_sync_minutes;
                        let nextAt = null;
                        if (isBenign && intervalMin && tournament.gotsport_last_synced_at) {
                            const last = new Date(tournament.gotsport_last_synced_at).getTime();
                            nextAt = new Date(last + intervalMin * 60_000);
                        }
                        return (
                            <div
                                className={`text-[10px] ${isBenign ? "text-zinc-500" : "text-heat"}`}
                                data-testid="gotsport-last-poll-reason"
                            >
                                {isBenign ? (
                                    <>Auto-sync waiting on interval{nextAt && <> · next tick around {nextAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</>}</>
                                ) : (
                                    <>Auto-sync last skipped: <span className="font-mono">{reason}</span>
                                        {tournament.gotsport_last_poll_checked_at && (
                                            <> · {new Date(tournament.gotsport_last_poll_checked_at).toLocaleString()}</>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })()}
                    {/* iter84.10 — Auto-sync interval picker. Backend
                        validates: requires start_date + end_date set on
                        the tournament; only valid for gotsport data
                        source; tenant-level daily cap applies. */}
                    <div className="flex items-center gap-2 pt-1">
                        <label className="text-[10px] uppercase tracking-widest text-zinc-500">Auto-sync</label>
                        <select
                            data-testid="gotsport-auto-sync-select"
                            value={tournament.gotsport_auto_sync_minutes || ""}
                            onChange={async (e) => {
                                const v = e.target.value;
                                try {
                                    await api.setGotsportAutoSync(
                                        tournament.id,
                                        v === "" ? null : parseInt(v, 10),
                                    );
                                    toast.success(v ? `Auto-sync set` : "Auto-sync turned off");
                                    onImported?.();
                                } catch (err) {
                                    toast.error(err?.response?.data?.detail || "Failed to update auto-sync");
                                }
                            }}
                            className="bg-ink border-2 border-[#333] rounded px-2 py-0.5 text-[11px] focus:border-neon focus:outline-none"
                        >
                            <option value="">Off (manual)</option>
                            <option value="30">Every 30 min</option>
                            <option value="60">Every 1 hour</option>
                            <option value="120">Every 2 hours</option>
                            <option value="360">Every 6 hours</option>
                            <option value="1440">Once daily</option>
                        </select>
                    </div>
                    {tournament.gotsport_last_sync_error && (
                        <div className="text-[10px] text-heat" data-testid="gotsport-last-sync-error">
                            Last error: {tournament.gotsport_last_sync_error}
                        </div>
                    )}
                    <Button
                        data-testid="gotsport-refresh-btn"
                        size="sm" variant="outline" type="button"
                        onClick={handleRefresh} disabled={busy === "refresh"}
                        className="text-xs"
                    >
                        <RotateCw className="w-3 h-3 mr-1" /> {busy === "refresh" ? "Refreshing…" : "Refresh scores"}
                    </Button>
                    {/* iter87.180.7 — Mobile-friendly diagnostic. Shows the
                        exact `w / d / l / pts` values the scraper parsed for
                        each standings row this tick. If FRAM shows here as
                        w=0 d=0 l=1 but the tile still shows w=1 d=1, the
                        stamp-side fix is the bug. If it shows w=1 d=1 here
                        too, the scraper is misreading the GotSport HTML. */}
                    {debug && (
                        <div
                            className="border-t-2 border-[#333] pt-2 mt-2 space-y-1.5"
                            data-testid="gotsport-refresh-debug"
                        >
                            <div className="text-[10px] uppercase tracking-widest text-neon font-bold">
                                Scraper output ({debug.standings_row_count} rows total)
                            </div>
                            <div className="text-[10px] text-zinc-500">
                                Synced: {new Date(debug.synced_at).toLocaleString()}
                            </div>
                            <div className="max-h-52 overflow-y-auto border-2 border-[#222] rounded p-1.5 space-y-0.5 font-mono text-[10px]">
                                {debug.standings.map((row, i) => (
                                    <div key={i} className="whitespace-nowrap">
                                        <span className="text-zinc-500">{row.bracket || "—"}</span>{" · "}
                                        <span className="text-white">{row.team}</span>{" · "}
                                        <span className="text-neon">MP={row.mp}</span>{" "}
                                        W={row.w} D={row.d} L={row.l}{" · "}
                                        GF={row.gf} GA={row.ga}{" · "}
                                        <span className="text-volt">PTS={row.pts}</span>
                                    </div>
                                ))}
                                {debug.standings.length === 0 && (
                                    <div className="text-heat">Scraper returned 0 rows.</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="flex gap-2 items-end">
                <div className="flex-1">
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500">GotSport Event ID</label>
                    <input
                        data-testid="gotsport-event-id-input"
                        type="number" value={eventId}
                        onChange={e => setEventId(e.target.value)}
                        placeholder="e.g. 53691"
                        className="w-full bg-ink border-2 border-[#333] rounded px-2 py-1 text-sm focus:border-neon focus:outline-none"
                    />
                </div>
                <Button
                    data-testid="gotsport-find-divisions-btn"
                    type="button" onClick={handleFindDivisions}
                    disabled={!eventId || busy === "find"}
                    className="brutal-btn-ghost text-xs"
                >
                    <Search className="w-3 h-3 mr-1" /> {busy === "find" ? "…" : "Find divisions"}
                </Button>
            </div>

            {event?.divisions && (
                <div className="space-y-2">
                    <div className="text-[10px] uppercase tracking-widest text-neon">
                        {event.event_name} · {event.start_date} → {event.end_date}
                    </div>
                    <select
                        data-testid="gotsport-division-select"
                        value={selectedGroupId}
                        onChange={e => { setSelectedGroupId(e.target.value); setPreview(null); }}
                        className="w-full bg-ink border-2 border-[#333] rounded px-2 py-1 text-sm focus:border-neon focus:outline-none"
                    >
                        {event.divisions.map(d => (
                            <option key={d.group_id} value={d.group_id}>{d.name}</option>
                        ))}
                    </select>
                    <Button
                        data-testid="gotsport-preview-btn"
                        type="button" onClick={handlePreview}
                        disabled={!selectedGroupId || busy === "preview"}
                        className="brutal-btn-ghost text-xs w-full"
                    >
                        {busy === "preview" ? "Loading preview…" : "Preview import"}
                    </Button>
                </div>
            )}

            {preview && (
                <div className="brutal-card-light p-2 space-y-2" data-testid="gotsport-preview">
                    <div className="text-[10px] uppercase tracking-widest text-neon">Preview</div>
                    <div className="text-xs"><span className="font-bold">{preview.division_name}</span></div>
                    <div className="text-xs flex gap-3">
                        <span>👥 {preview.team_count} teams</span>
                        <span>⚽ {preview.match_count} matches</span>
                        <span>✅ {preview.played_count} already played</span>
                    </div>
                    <div className="text-[10px] text-zinc-500">
                        Sample teams: {preview.sample_teams.join(", ")}
                    </div>
                    <div className="flex items-start gap-2 text-[10px] text-volt p-1.5 border border-volt/40 rounded">
                        <AlertTriangle className="w-3 h-3 mt-px shrink-0" />
                        <span>
                            By importing, you confirm you have permission to use this public schedule
                            for your rivalry. Importing will wipe + replace this tournament's teams,
                            matches, and schedule.
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            data-testid="gotsport-cancel-btn"
                            size="sm" variant="ghost" type="button"
                            onClick={() => setPreview(null)}
                            disabled={busy === "import"}
                        >Cancel</Button>
                        <Button
                            data-testid="gotsport-confirm-import-btn"
                            size="sm" type="button"
                            onClick={() => setConfirmOpen(true)}
                            disabled={busy === "import"}
                            className="brutal-btn-primary"
                        >
                            {busy === "import" ? "Importing…" : "Confirm + Wipe + Import"}
                        </Button>
                    </div>
                </div>
            )}

            {/* iter87.172 — In-app confirm modal (replaces window.confirm
                which mobile Safari + Chrome silently blocked). Backdrop
                click and Cancel both dismiss; only "Yes, wipe & import"
                fires the import. */}
            {confirmOpen && preview && (
                <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
                    onClick={() => setConfirmOpen(false)}
                    data-testid="gotsport-confirm-modal"
                >
                    <div
                        className="brutal-card max-w-md w-full p-5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start gap-3 mb-3">
                            <AlertTriangle className="w-5 h-5 text-heat shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-bold uppercase tracking-widest text-white mb-1">Wipe + Import?</h3>
                                <p className="text-[12px] text-zinc-400 leading-snug">
                                    This will <span className="text-heat font-bold">WIPE</span> your current teams + matches + schedule and replace them with{" "}
                                    <span className="text-white font-bold">{preview.team_count}</span> teams and{" "}
                                    <span className="text-white font-bold">{preview.match_count}</span> matches from GotSport.
                                </p>
                                <p className="text-[11px] text-zinc-500 italic mt-2">This cannot be undone.</p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <Button
                                size="sm" variant="ghost" type="button"
                                onClick={() => setConfirmOpen(false)}
                                data-testid="gotsport-confirm-modal-cancel"
                            >Cancel</Button>
                            <Button
                                size="sm" type="button"
                                onClick={handleImport}
                                data-testid="gotsport-confirm-modal-proceed"
                                className="brutal-btn-primary"
                            >
                                Yes, wipe & import
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
